import { useEffect, useRef, useState } from "react";
import { Pencil, Eraser, Trash2, X } from "lucide-react";

// Ekran ustiga chizib tushuntirish vositasi — barcha foydalanuvchilarga
// ko'rinadi. Faqat notebook/desktop'da kerak (telefon ekranida joy tor,
// kerak emas deb aytilgan) — shuning uchun "hidden lg:block" bilan
// o'ralgan.

const COLORS = ["#EF4444", "#3B82F6", "#22C55E", "#F59E0B"];
const SIZES = [3, 6, 10];

export default function TeacherDrawOverlay() {
  const [active, setActive] = useState(false);
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(SIZES[1]);
  const [erasing, setErasing] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  // Canvas'ni ekran o'lchamiga moslashtirish
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const ctx = canvas.getContext("2d");
      const prev = ctx?.getImageData(0, 0, canvas.width, canvas.height);
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      if (ctx && prev) ctx.putImageData(prev, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [active]);

  const getPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawingRef.current = true;
    lastPointRef.current = getPoint(e);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !lastPointRef.current) return;

    const point = getPoint(e);
    ctx.globalCompositeOperation = erasing
      ? "destination-out"
      : "source-over";
    ctx.strokeStyle = color;
    ctx.lineWidth = erasing ? size * 3 : size;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();

    lastPointRef.current = point;
  };

  const stopDrawing = () => {
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const close = () => {
    setActive(false);
    setErasing(false);
  };

  return (
    <div className="hidden lg:block">
      {/* Chizish rejimi yoqilganda — butun ekranni qoplaydigan canvas */}
      {active && (
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDrawing}
          onPointerLeave={stopDrawing}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            cursor: erasing ? "cell" : "crosshair",
            touchAction: "none",
          }}
        />
      )}

      {!active ? (
        // Boshlash tugmasi (qalam)
        <button
          onClick={() => setActive(true)}
          title="Chizib tushuntirish"
          style={{
            position: "fixed",
            left: 20,
            bottom: 90,
            zIndex: 9999,
            width: 52,
            height: 52,
            borderRadius: "50%",
            background: "#111827",
            border: "1px solid rgba(255,255,255,.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 24px rgba(0,0,0,.4)",
            cursor: "pointer",
          }}>
          <Pencil size={20} style={{ color: "#F59E0B" }} />
        </button>
      ) : (
        // Asboblar paneli
        <div
          style={{
            position: "fixed",
            left: "50%",
            bottom: 20,
            transform: "translateX(-50%)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 14px",
            borderRadius: 999,
            background: "#111827",
            border: "1px solid rgba(255,255,255,.12)",
            boxShadow: "0 8px 24px rgba(0,0,0,.4)",
          }}>
          {/* Ranglar */}
          <div style={{ display: "flex", gap: 8 }}>
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => {
                  setColor(c);
                  setErasing(false);
                }}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: c,
                  border:
                    !erasing && color === c ?
                      "2px solid #fff"
                    : "2px solid transparent",
                  cursor: "pointer",
                  padding: 0,
                }}
              />
            ))}
          </div>

          <div
            style={{
              width: 1,
              height: 20,
              background: "rgba(255,255,255,.15)",
            }}
          />

          {/* Qalam qalinligi */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {SIZES.map((s) => (
              <button
                key={s}
                onClick={() => setSize(s)}
                title={`${s}px`}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  background: "transparent",
                  border:
                    size === s ?
                      "1px solid rgba(255,255,255,.6)"
                    : "1px solid transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}>
                <span
                  style={{
                    width: s + 2,
                    height: s + 2,
                    borderRadius: "50%",
                    background: "#fff",
                  }}
                />
              </button>
            ))}
          </div>

          <div
            style={{
              width: 1,
              height: 20,
              background: "rgba(255,255,255,.15)",
            }}
          />

          {/* O'chirg'ich */}
          <button
            onClick={() => setErasing((prev) => !prev)}
            title="O'chirg'ich"
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: erasing ? "rgba(255,255,255,.12)" : "transparent",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}>
            <Eraser size={15} style={{ color: "#fff" }} />
          </button>

          {/* Tozalash */}
          <button
            onClick={clearCanvas}
            title="Hammasini tozalash"
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: "transparent",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}>
            <Trash2 size={15} style={{ color: "#F87171" }} />
          </button>

          {/* Yopish */}
          <button
            onClick={close}
            title="Yopish"
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: "transparent",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}>
            <X size={16} style={{ color: "#fff" }} />
          </button>
        </div>
      )}
    </div>
  );
}
